import { useEffect, useId, useLayoutEffect, useRef, type ReactNode } from "react";
import { ChevronDownIcon } from "@/components/icons";
import { actionProps, type ControlContext } from "@/lib/controlAnalytics";
import type { ControlAction } from "@/lib/controlActions";
import { filterButtonClass } from "./controls";

/** The least room between a panel and the window's edge, in px. */
const WINDOW_GUTTER = 16;
/** How near the arrow comes to the panel's corners, in px. */
const ARROW_INSET = 20;
/** Half the arrow's width, in px. */
const ARROW_HALF = 6;
/** A press on one of these takes focus for itself. */
const FOCUSABLE = "a[href], button, input, select, textarea, summary, [tabindex]";

/**
 * Scrolls a panel up clear of the window's foot, by its `scroll-margin-bottom`:
 * on a phone, the tab bar. `scrollIntoView` will not do it, since Chrome leaves
 * alone a box that fits the window, however much of it the bar covers. A
 * panel in a scrolling box of its own, such as a dialog's body, is first
 * scrolled into that box.
 */
function reveal(element: HTMLElement) {
    element.scrollIntoView?.({ block: "nearest" });
    const room = Number.parseFloat(getComputedStyle(element).scrollMarginBottom) || 0;
    const hidden = element.getBoundingClientRect().bottom + room - window.innerHeight;
    if (hidden > 0) window.scrollBy({ top: hidden });
}

/**
 * A filter's button and the panel it opens: a labelled dialog that does not
 * take over the page, since it can hold form fields, which a menu cannot.
 * The caller says which is open, so only one ever is. Escape, a press outside
 * or tabbing away closes it. Focus goes into the panel, and back to the button
 * unless it has gone on to something else. The panel slides sideways to stay
 * inside the window.
 */
export default function FilterPopover({
    label,
    summary,
    selected = summary !== null,
    analyticsField,
    wide = false,
    field = false,
    open,
    onOpenChange,
    children,
}: {
    label: string;
    /** What the filter holds, shown in place of its name. Null when it holds nothing. */
    summary: string | null;
    /** The button reads as in force. By default, whenever it holds something. */
    selected?: boolean;
    analyticsField: string;
    wide?: boolean;
    /** Drawn as a form's select: the full width, the chevron at the far end. */
    field?: boolean;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    children: (close: () => void) => ReactNode;
}) {
    const box = useRef<HTMLSpanElement>(null);
    const trigger = useRef<HTMLButtonElement>(null);
    const panel = useRef<HTMLDivElement>(null);
    const wasOpen = useRef(false);
    const tabbedAway = useRef(false);
    const panelId = useId();

    // A panel that closes around the focus leaves it on the body. Focus on
    // its way to the next control passes through the body too, and goes on.
    useEffect(() => {
        const lost = document.activeElement === document.body;
        if (wasOpen.current && !open && lost && !tabbedAway.current) trigger.current?.focus();
        wasOpen.current = open;
        tabbedAway.current = false;
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const away = (event: MouseEvent) => {
            const target = event.target as Element;
            if (box.current?.contains(target)) return;
            onOpenChange(false);
            // Left alone, a press on nothing would take focus off the button again.
            if (!target.closest(FOCUSABLE)) event.preventDefault();
        };
        const escape = (event: KeyboardEvent) => {
            if (event.key !== "Escape") return;
            // Taken here, so a dialog the panel sits in stays open.
            event.preventDefault();
            onOpenChange(false);
        };
        document.addEventListener("mousedown", away);
        document.addEventListener("keydown", escape);
        return () => {
            document.removeEventListener("mousedown", away);
            document.removeEventListener("keydown", escape);
        };
    }, [open, onOpenChange]);

    useLayoutEffect(() => {
        if (!open) return;
        const place = () => {
            const element = panel.current;
            const button = trigger.current?.getBoundingClientRect();
            if (!element || !button) return;
            element.style.translate = "";
            const { left, right, width } = element.getBoundingClientRect();
            const windowWidth = document.documentElement.clientWidth;
            const shift = Math.max(
                WINDOW_GUTTER - left,
                Math.min(0, windowWidth - WINDOW_GUTTER - right),
            );
            element.style.translate = `${shift}px`;
            const arrow = button.left + button.width / 2 - (left + shift) - ARROW_HALF;
            element.style.setProperty(
                "--filter-arrow",
                `${Math.min(Math.max(arrow, ARROW_INSET), width - ARROW_INSET)}px`,
            );
        };
        place();
        if (panel.current) reveal(panel.current);
        panel.current?.focus({ preventScroll: true });
        window.addEventListener("resize", place);
        return () => window.removeEventListener("resize", place);
    }, [open]);

    return (
        <span
            ref={box}
            className={`relative min-w-0 max-w-full ${field ? "flex w-full" : "inline-flex"}`}
            onBlur={(event) => {
                const next = event.relatedTarget;
                if (open && next instanceof Node && !event.currentTarget.contains(next)) {
                    tabbedAway.current = true;
                    onOpenChange(false);
                }
            }}
        >
            <button
                {...actionProps("filter_menu_toggled", {
                    source: "FilterPopover",
                    field: analyticsField,
                    next_state: !open,
                })}
                ref={trigger}
                type="button"
                aria-label={summary === null ? label : `${label}: ${summary}`}
                aria-haspopup="dialog"
                aria-expanded={open}
                aria-controls={open ? panelId : undefined}
                onClick={() => onOpenChange(!open)}
                className={`${filterButtonClass(selected)} ${field ? "w-full justify-between" : ""}`}
            >
                <span className="truncate">{summary ?? label}</span>
                <ChevronDownIcon
                    aria-hidden="true"
                    className={`size-3.5 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                />
            </button>
            {open && (
                <div
                    ref={panel}
                    id={panelId}
                    role="dialog"
                    aria-label={label}
                    tabIndex={-1}
                    className={`absolute left-0 top-[calc(100%+10px)] z-20 scroll-mb-4 rounded-control border border-line bg-card shadow-pop outline-none max-[760px]:scroll-mb-20 ${
                        wide ? "w-[min(360px,calc(100vw-32px))]" : "w-[min(260px,calc(100vw-32px))]"
                    }`}
                >
                    <span
                        aria-hidden="true"
                        className="absolute -top-[7px] left-[var(--filter-arrow)] size-3 rotate-45 border-l border-t border-line bg-card"
                    />
                    <div className="relative max-h-[min(440px,calc(100dvh-176px))] overflow-y-auto rounded-control">
                        {children(() => onOpenChange(false))}
                    </div>
                </div>
            )}
        </span>
    );
}

/** One way a popover's choices are ticked: one of them, or any of them. */
export function FilterChoice({
    type,
    name,
    checked,
    action,
    context,
    onChange,
    children,
    count,
}: {
    type: "radio" | "checkbox";
    name: string;
    checked: boolean;
    action: ControlAction;
    context: ControlContext;
    onChange: () => void;
    children: ReactNode;
    count?: ReactNode;
}) {
    return (
        <label className="flex min-h-9 cursor-pointer items-center gap-2.5 rounded-[9px] px-2.5 text-[13px] text-fg transition-colors hover:bg-hover max-[760px]:min-h-11">
            <input
                {...actionProps(action, context)}
                type={type}
                name={name}
                checked={checked}
                onChange={onChange}
                className="size-4 shrink-0 accent-brand-ink"
            />
            <span className="min-w-0 flex-1 truncate">{children}</span>
            {count}
        </label>
    );
}
